import { calculateBaseScale } from './shapeUtils';

export const drawImage = (ctx, img, rect, scale, position) => {
    const displayWidth = rect.width;
    const displayHeight = rect.height;
    
    const scaleWidth = displayWidth / img.width;
    const scaleHeight = displayHeight / img.height;
    const baseScale = Math.min(scaleWidth, scaleHeight);
    const finalScale = baseScale * scale;
    
    const width = img.width * finalScale;
    const height = img.height * finalScale;
    
    const x = (displayWidth - width) / 2 + position.x;
    const y = (displayHeight - height) / 2 + position.y;
    
    ctx.drawImage(img, x, y, width, height);
};

export const drawShape = (ctx, shape, scale, isCurrentShape = false, layerId = null, editingLayer = null) => {
    console.log('Drawing shape:', shape, 'isCurrentShape:', isCurrentShape, 'layerId:', layerId, 'editingLayer:', editingLayer);
    
    ctx.beginPath();
    ctx.strokeStyle = shape.color;
    ctx.lineWidth = shape.lineWidth / scale;

    // Apply shape offset
    const offsetX = shape.offsetX || 0;
    const offsetY = shape.offsetY || 0;
    ctx.translate(offsetX, offsetY);

    // Draw the actual shape
    switch (shape.type) {
        case 'textbox':
            console.log('Drawing textbox:', shape);
            // Draw background
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(
                shape.startX,
                shape.startY,
                shape.width,
                shape.height
            );
            
            // Draw border
            ctx.strokeStyle = '#666666';
            ctx.lineWidth = 1 / scale;
            ctx.strokeRect(
                shape.startX,
                shape.startY,
                shape.width,
                shape.height
            );
            
            // Draw text with wrapping
            ctx.fillStyle = shape.color || '#000000';
            const fontSize = shape.fontSize || 24;
            ctx.font = `${fontSize/scale}px Arial`;
            ctx.textBaseline = 'top';
            
            const padding = 5/scale;
            const maxWidth = shape.width - (padding * 2);
            const lineHeight = fontSize/scale * 1.2;
            
            // Split content into lines first (handle manual line breaks)
            const lines = (shape.content || 'Click to edit').split('\n');
            let y = shape.startY + padding;
            
            // Process each line for word wrapping
            for (let line of lines) {
                const words = line.split(' ');
                let currentLine = '';
                
                for (let word of words) {
                    const testLine = currentLine + (currentLine ? ' ' : '') + word;
                    const metrics = ctx.measureText(testLine);
                    
                    if (metrics.width > maxWidth && currentLine !== '') {
                        ctx.fillText(currentLine, shape.startX + padding, y);
                        currentLine = word;
                        y += lineHeight;
                        
                        // Stop if we've exceeded the box height
                        if (y + lineHeight > shape.startY + shape.height - padding) {
                            return;  // Exit early if we're out of space
                        }
                    } else {
                        currentLine = testLine;
                    }
                }
                
                // Draw the remaining text for this line
                if (currentLine && y + lineHeight <= shape.startY + shape.height - padding) {
                    ctx.fillText(currentLine, shape.startX + padding, y);
                }
                
                // Move to next line
                y += lineHeight;
                
                // Stop if we've exceeded the box height
                if (y + lineHeight > shape.startY + shape.height - padding) {
                    break;
                }
            }
            break;
        case 'freedraw':
            if (shape.points && shape.points.length > 1) {
                // Always draw points directly, whether current or completed
                ctx.moveTo(shape.points[0].x, shape.points[0].y);
                for (let i = 1; i < shape.points.length; i++) {
                    ctx.lineTo(shape.points[i].x, shape.points[i].y);
                }
            }
            break;
        case 'line':
            drawLine(ctx, shape);
            break;
        case 'rectangle':
            drawRectangle(ctx, shape);
            break;
        case 'oval':
            drawOval(ctx, shape);
            break;
        case 'marker':
            // Draw filled circle
            ctx.beginPath();
            const radius = shape.width / 2;  // Use width as diameter
            ctx.arc(
                shape.startX + radius,
                shape.startY + radius,
                radius,
                0,
                Math.PI * 2
            );
            ctx.fillStyle = shape.color;
            ctx.fill();
            
            // Draw border
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 1 / scale;
            ctx.stroke();
            break;
        default:
            break;
    }
    ctx.stroke();

    // Draw editor box if needed
    if (isCurrentShape || layerId === editingLayer) {
        drawEditorBox(ctx, shape, scale);
    }

    // Reset the translation
    ctx.translate(-offsetX, -offsetY);
};

const drawLine = (ctx, shape) => {
    ctx.moveTo(shape.startX, shape.startY);
    ctx.lineTo(shape.startX + (shape.width || 0), shape.startY + (shape.height || 0));
};

const drawRectangle = (ctx, shape) => {
    ctx.rect(
        shape.startX,
        shape.startY,
        shape.width || 0,
        shape.height || 0
    );
};

const drawOval = (ctx, shape) => {
    const centerX = shape.startX + (shape.width || 0) / 2;
    const centerY = shape.startY + (shape.height || 0) / 2;
    const radiusX = Math.abs((shape.width || 0) / 2);
    const radiusY = Math.abs((shape.height || 0) / 2);
    
    ctx.ellipse(
        centerX,
        centerY,
        radiusX,
        radiusY,
        0,
        0,
        Math.PI * 2
    );
};

const drawEditorBox = (ctx, shape, scale) => {
    ctx.save();
    
    // Editor box style
    ctx.strokeStyle = '#4a86e8';
    ctx.setLineDash([8, 8]);
    ctx.lineWidth = 2;
    
    // Calculate bounding box
    const boxX = shape.startX;
    const boxY = shape.startY;
    const boxWidth = shape.width || 0;
    const boxHeight = shape.height || 0;
    
    // Add extra padding for marker type
    const padding = shape.type === 'marker' ? 24 : 4;
    
    // Draw the editor box with padding
    ctx.strokeRect(
        boxX - padding,
        boxY - padding,
        boxWidth + padding * 2,
        boxHeight + padding * 2
    );
    
    // Draw handles with the same padding offset
    drawHandles(ctx, 
        boxX - padding, 
        boxY - padding, 
        boxWidth + padding * 2, 
        boxHeight + padding * 2
    );
    
    ctx.restore();
};

const drawResizeHandles = (ctx, x, y, width, height, scale) => {
    // Implementation of drawResizeHandles function
};

const drawHandles = (ctx, boxX, boxY, boxWidth, boxHeight) => {
    const handles = [
        // Corner handles - larger size for prominence
        { x: boxX, y: boxY, type: 'corner-tl', size: 14 },
        { x: boxX + boxWidth, y: boxY, type: 'corner-tr', size: 14 },
        { x: boxX, y: boxY + boxHeight, type: 'corner-bl', size: 14 },
        { x: boxX + boxWidth, y: boxY + boxHeight, type: 'corner-br', size: 14 },
        
        // Edge handles
        { x: boxX, y: boxY + boxHeight/2, type: 'edge-h', size: 10 },
        { x: boxX + boxWidth, y: boxY + boxHeight/2, type: 'edge-h-right', size: 10 },
        { x: boxX + boxWidth/2, y: boxY, type: 'edge-v', size: 10 },
        { x: boxX + boxWidth/2, y: boxY + boxHeight, type: 'edge-v-bottom', size: 10 },
        
        // Center handle
        { x: boxX + boxWidth/2, y: boxY + boxHeight/2, type: 'center', size: 12 }
    ];

    handles.forEach(handle => drawHandle(ctx, handle));
};

const drawHandle = (ctx, handle) => {
    // Draw white border
    ctx.fillStyle = 'white';
    ctx.fillRect(
        handle.x - handle.size/2 - 1,
        handle.y - handle.size/2 - 1,
        handle.size + 2,
        handle.size + 2
    );
    
    // Draw handle with different styles based on type
    ctx.fillStyle = handle.type === 'center' ? '#ff4444' : // Red for center
                   handle.type.startsWith('corner') ? '#4a86e8' : // Blue for corners
                   '#44ff44'; // Green for edges
    
    if (handle.type === 'center') {
        ctx.beginPath();
        ctx.arc(handle.x, handle.y, handle.size/2 - 1, 0, Math.PI * 2);
        ctx.fill();
    } else {
        ctx.fillRect(
            handle.x - handle.size/2,
            handle.y - handle.size/2,
            handle.size,
            handle.size
        );
    }
}; 